import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, Code, Eye, EyeOff, Filter, Search, Zap, Clock, Webhook, Database, Play, Check, X, AlertTriangle, Info, Copy, FileText, Settings } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';

// Types based on the schema
interface UserJourneyTrigger {
  id: number;
  key: string;
  name: string;
  description?: string;
  source: 'crm' | 'webhook' | 'time';
  sourceEventTypes: string[];
  payloadSchema?: any;
  defaultFilter?: any;
  status: 'active' | 'disabled';
  createdAt: string;
  updatedAt: string;
}

interface TriggerListResponse {
  triggers: UserJourneyTrigger[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Form validation schema
const triggerFormSchema = z.object({
  key: z.string()
    .min(1, "Key is required")
    .max(255, "Key must be less than 255 characters")
    .regex(/^[a-z0-9_-]+$/, "Key must contain only lowercase letters, numbers, underscores, and hyphens"),
  name: z.string()
    .min(1, "Name is required")
    .max(255, "Name must be less than 255 characters"),
  description: z.string().optional(),
  source: z.enum(['crm', 'webhook', 'time'], {
    required_error: "Source is required"
  }),
  sourceEventTypes: z.array(z.string()).min(1, "At least one event type is required"),
  payloadSchema: z.string().optional().refine((val) => {
    if (!val || val.trim() === '') return true;
    try {
      JSON.parse(val);
      return true;
    } catch {
      return false;
    }
  }, "Must be valid JSON"),
  defaultFilter: z.string().optional().refine((val) => {
    if (!val || val.trim() === '') return true;
    try {
      JSON.parse(val);
      return true;
    } catch {
      return false;
    }
  }, "Must be valid JSON"),
  status: z.enum(['active', 'disabled']).default('active'),
});

type TriggerFormData = z.infer<typeof triggerFormSchema>;

// Predefined event types for different sources
const EVENT_TYPES = {
  crm: [
    'user_registered',
    'share_verified', 
    'profile_tier_completed',
    'segment_assigned',
    'competition_entered',
    'profile_updated',
    'user_inactive',
    'purchase_completed'
  ],
  webhook: [
    'payment_completed',
    'subscription_created',
    'subscription_cancelled',
    'external_event',
    'api_trigger',
    'third_party_integration'
  ],
  time: [
    'daily_trigger',
    'weekly_trigger',
    'monthly_trigger',
    'scheduled_event',
    'anniversary',
    'reminder'
  ]
};

export default function AdminTriggerManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // State management
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState<UserJourneyTrigger | null>(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [previewTrigger, setPreviewTrigger] = useState<UserJourneyTrigger | null>(null);
  const [showJsonPreview, setShowJsonPreview] = useState<{ [key: number]: boolean }>({});
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    status: '',
    source: '',
    search: '',
    page: 1,
    limit: 20
  });

  // Form handling
  const form = useForm<TriggerFormData>({
    resolver: zodResolver(triggerFormSchema),
    defaultValues: {
      key: '',
      name: '',
      description: '',
      source: 'crm',
      sourceEventTypes: [],
      payloadSchema: '',
      defaultFilter: '',
      status: 'active'
    }
  });

  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [customEventType, setCustomEventType] = useState('');

  // Reset form when dialog closes
  useEffect(() => {
    if (!isCreateDialogOpen && !isEditDialogOpen) {
      form.reset();
      setSelectedEventTypes([]);
      setCustomEventType('');
      setEditingTrigger(null);
    }
  }, [isCreateDialogOpen, isEditDialogOpen, form]);

  // Fetch triggers
  const { data: triggersData, isLoading, error } = useQuery<TriggerListResponse>({
    queryKey: ['/api/admin/triggers', filters],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (filters.status) searchParams.append('status', filters.status);
      if (filters.source) searchParams.append('source', filters.source);
      searchParams.append('page', filters.page.toString());
      searchParams.append('limit', filters.limit.toString());
      
      const response = await apiRequest('GET', `/api/admin/triggers?${searchParams.toString()}`);
      return response.json() as Promise<TriggerListResponse>;
    }
  });

  // Create trigger mutation
  const createMutation = useMutation({
    mutationFn: async (data: TriggerFormData) => {
      const payload = {
        ...data,
        sourceEventTypes: data.sourceEventTypes,
        payloadSchema: data.payloadSchema ? JSON.parse(data.payloadSchema) : undefined,
        defaultFilter: data.defaultFilter ? JSON.parse(data.defaultFilter) : undefined,
      };
      const response = await apiRequest('POST', '/api/admin/triggers', { body: payload });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/triggers'] });
      setIsCreateDialogOpen(false);
      toast({
        title: "Success",
        description: "Trigger created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Create trigger error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create trigger",
        variant: "destructive",
      });
    }
  });

  // Update trigger mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: TriggerFormData }) => {
      const payload = {
        ...data,
        sourceEventTypes: data.sourceEventTypes,
        payloadSchema: data.payloadSchema ? JSON.parse(data.payloadSchema) : undefined,
        defaultFilter: data.defaultFilter ? JSON.parse(data.defaultFilter) : undefined,
      };
      const response = await apiRequest('PATCH', `/api/admin/triggers/${id}`, { body: payload });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/triggers'] });
      setIsEditDialogOpen(false);
      toast({
        title: "Success",
        description: "Trigger updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('Update trigger error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update trigger",
        variant: "destructive",
      });
    }
  });

  // Delete trigger mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('DELETE', `/api/admin/triggers/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/triggers'] });
      toast({
        title: "Success",
        description: "Trigger deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error('Delete trigger error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete trigger",
        variant: "destructive",
      });
    }
  });

  // Preview trigger evaluation mutation
  const previewMutation = useMutation({
    mutationFn: async ({ triggerId, testData }: { triggerId: number; testData: any }) => {
      const response = await apiRequest('POST', '/api/admin/triggers/preview-eval', {
        body: {
          triggerId,
          payload: testData
        }
      });
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Preview Completed",
        description: `Trigger evaluation: ${data.matched ? 'MATCH' : 'NO MATCH'}`,
      });
    },
    onError: (error: any) => {
      console.error('Preview trigger error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to preview trigger",
        variant: "destructive",
      });
    }
  });

  // Check inactive users mutation
  const checkInactiveUsersMutation = useMutation({
    mutationFn: async ({ days }: { days: number }) => {
      const response = await apiRequest('GET', `/api/admin/inactive-users?days=${days}&limit=100`);
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Inactive Users Found",
        description: `Found ${data.users.length} users inactive for ${data.criteria.daysInactive}+ days`,
      });
    },
    onError: (error: any) => {
      console.error('Check inactive users error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to check inactive users",
        variant: "destructive",
      });
    }
  });

  // Trigger inactive user campaign
  const triggerInactiveUsersMutation = useMutation({
    mutationFn: async ({ campaignId, daysInactive, dryRun }: { campaignId: number; daysInactive: number; dryRun: boolean }) => {
      const response = await apiRequest('POST', '/api/admin/trigger-inactive-users', {
        body: {
          campaignId,
          daysInactive,
          dryRun
        }
      });
      return response.json();
    },
    onSuccess: (data) => {
      if (data.dryRun) {
        toast({
          title: "Dry Run Complete",
          description: `Would enroll ${data.usersFound} inactive users in campaign`,
        });
      } else {
        toast({
          title: "Users Enrolled",
          description: `Successfully enrolled ${data.enrolled} users, ${data.failed} failed`,
        });
      }
    },
    onError: (error: any) => {
      console.error('Trigger inactive users error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to trigger inactive user campaign",
        variant: "destructive",
      });
    }
  });

  // Event handlers
  const handleCreateTrigger = () => {
    setIsCreateDialogOpen(true);
    form.reset();
  };

  const handleEditTrigger = (trigger: UserJourneyTrigger) => {
    setEditingTrigger(trigger);
    setSelectedEventTypes(trigger.sourceEventTypes || []);
    form.reset({
      key: trigger.key,
      name: trigger.name,
      description: trigger.description || '',
      source: trigger.source,
      sourceEventTypes: trigger.sourceEventTypes || [],
      payloadSchema: trigger.payloadSchema ? JSON.stringify(trigger.payloadSchema, null, 2) : '',
      defaultFilter: trigger.defaultFilter ? JSON.stringify(trigger.defaultFilter, null, 2) : '',
      status: trigger.status
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteTrigger = (id: number) => {
    deleteMutation.mutate(id);
  };

  const handlePreviewTrigger = (trigger: UserJourneyTrigger) => {
    setPreviewTrigger(trigger);
    setIsPreviewDialogOpen(true);
  };

  const onSubmit = (data: TriggerFormData) => {
    const finalData = {
      ...data,
      sourceEventTypes: selectedEventTypes.length > 0 ? selectedEventTypes : data.sourceEventTypes
    };

    if (editingTrigger) {
      updateMutation.mutate({ id: editingTrigger.id, data: finalData });
    } else {
      createMutation.mutate(finalData);
    }
  };

  const addEventType = (eventType: string) => {
    if (eventType && !selectedEventTypes.includes(eventType)) {
      const newEventTypes = [...selectedEventTypes, eventType];
      setSelectedEventTypes(newEventTypes);
      form.setValue('sourceEventTypes', newEventTypes);
    }
  };

  const removeEventType = (eventType: string) => {
    const newEventTypes = selectedEventTypes.filter(type => type !== eventType);
    setSelectedEventTypes(newEventTypes);
    form.setValue('sourceEventTypes', newEventTypes);
  };

  const addCustomEventType = () => {
    if (customEventType && !selectedEventTypes.includes(customEventType)) {
      addEventType(customEventType);
      setCustomEventType('');
    }
  };

  const toggleJsonPreview = (triggerId: number) => {
    setShowJsonPreview(prev => ({
      ...prev,
      [triggerId]: !prev[triggerId]
    }));
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'crm':
        return <Database className="h-4 w-4" />;
      case 'webhook':
        return <Webhook className="h-4 w-4" />;
      case 'time':
        return <Clock className="h-4 w-4" />;
      default:
        return <Zap className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  // Filter the triggers based on search
  const filteredTriggers = triggersData?.triggers?.filter(trigger => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        trigger.name.toLowerCase().includes(searchLower) ||
        trigger.key.toLowerCase().includes(searchLower) ||
        trigger.description?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  }) || [];

  return (
    <TooltipProvider>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Trigger Management</h1>
            <p className="text-muted-foreground">
              Create and manage automated triggers for user journey campaigns
            </p>
          </div>
          <Button onClick={handleCreateTrigger} data-testid="button-create-trigger">
            <Plus className="mr-2 h-4 w-4" />
            Create Trigger
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Filter className="mr-2 h-4 w-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col space-y-4 md:flex-row md:space-x-4 md:space-y-0">
              <div className="flex-1">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Search triggers..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                    data-testid="input-search-triggers"
                  />
                </div>
              </div>
              <div className="w-full md:w-48">
                <Label htmlFor="status-filter">Status</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger data-testid="select-status-filter">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full md:w-48">
                <Label htmlFor="source-filter">Source</Label>
                <Select
                  value={filters.source}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, source: value }))}
                >
                  <SelectTrigger data-testid="select-source-filter">
                    <SelectValue placeholder="All sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    <SelectItem value="crm">CRM</SelectItem>
                    <SelectItem value="webhook">Webhook</SelectItem>
                    <SelectItem value="time">Time-based</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load triggers: {error.message}
            </AlertDescription>
          </Alert>
        )}

        {/* Triggers List */}
        <Card>
          <CardHeader>
            <CardTitle>
              Triggers
              {triggersData && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({filteredTriggers.length} of {triggersData.pagination.total})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-muted-foreground">Loading triggers...</p>
                </div>
              </div>
            ) : filteredTriggers.length === 0 ? (
              <div className="text-center py-8">
                <Zap className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No triggers found</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {filters.search || filters.status || filters.source
                    ? "Try adjusting your filters"
                    : "Get started by creating your first trigger"}
                </p>
                {!filters.search && !filters.status && !filters.source && (
                  <div className="mt-6">
                    <Button onClick={handleCreateTrigger}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Trigger
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredTriggers.map((trigger) => (
                  <Card key={trigger.id} className="border-l-4 border-l-blue-500">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-2">
                              {getSourceIcon(trigger.source)}
                              <h3 className="font-semibold text-lg" data-testid={`text-trigger-name-${trigger.id}`}>
                                {trigger.name}
                              </h3>
                            </div>
                            <Badge className={getStatusColor(trigger.status)} data-testid={`badge-status-${trigger.id}`}>
                              {trigger.status}
                            </Badge>
                            <Badge variant="outline" data-testid={`badge-source-${trigger.id}`}>
                              {trigger.source}
                            </Badge>
                          </div>
                          
                          <div className="text-sm text-muted-foreground">
                            <strong>Key:</strong> <code className="bg-muted px-1 rounded">{trigger.key}</code>
                          </div>
                          
                          {trigger.description && (
                            <p className="text-sm text-muted-foreground" data-testid={`text-description-${trigger.id}`}>
                              {trigger.description}
                            </p>
                          )}
                          
                          <div className="flex flex-wrap gap-1">
                            {trigger.sourceEventTypes.map((eventType) => (
                              <Badge key={eventType} variant="secondary" className="text-xs">
                                {eventType}
                              </Badge>
                            ))}
                          </div>

                          {/* JSON Preview */}
                          {showJsonPreview[trigger.id] && (
                            <div className="mt-4 space-y-2">
                              {trigger.payloadSchema && (
                                <div>
                                  <Label className="text-xs font-semibold">Payload Schema:</Label>
                                  <pre className="bg-muted p-2 rounded text-xs overflow-x-auto">
                                    {JSON.stringify(trigger.payloadSchema, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {trigger.defaultFilter && (
                                <div>
                                  <Label className="text-xs font-semibold">Default Filter:</Label>
                                  <pre className="bg-muted p-2 rounded text-xs overflow-x-auto">
                                    {JSON.stringify(trigger.defaultFilter, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="text-xs text-muted-foreground">
                            Created: {new Date(trigger.createdAt).toLocaleString()}
                            {trigger.updatedAt !== trigger.createdAt && (
                              <span> • Updated: {new Date(trigger.updatedAt).toLocaleString()}</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleJsonPreview(trigger.id)}
                                data-testid={`button-toggle-json-${trigger.id}`}
                              >
                                {showJsonPreview[trigger.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {showJsonPreview[trigger.id] ? "Hide JSON" : "Show JSON"}
                            </TooltipContent>
                          </Tooltip>
                          
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreviewTrigger(trigger)}
                                data-testid={`button-preview-${trigger.id}`}
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Preview Trigger</TooltipContent>
                          </Tooltip>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditTrigger(trigger)}
                            data-testid={`button-edit-${trigger.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                data-testid={`button-delete-${trigger.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Trigger</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete the trigger "{trigger.name}"?
                                  This action cannot be undone and may affect active campaigns.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteTrigger(trigger.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {triggersData && triggersData.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {triggersData.pagination.page} of {triggersData.pagination.totalPages}
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                disabled={triggersData.pagination.page <= 1}
                onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                disabled={triggersData.pagination.page >= triggersData.pagination.totalPages}
                onClick={() => setFilters(prev => ({ ...prev, page: Math.min(triggersData.pagination.totalPages, prev.page + 1) }))}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTrigger ? 'Edit Trigger' : 'Create New Trigger'}
              </DialogTitle>
              <DialogDescription>
                Configure trigger conditions and settings for automated journey campaigns.
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="basic">Basic Settings</TabsTrigger>
                    <TabsTrigger value="events">Event Types</TabsTrigger>
                    <TabsTrigger value="advanced">Advanced</TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="key"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Trigger Key</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g., user_onboarding_start"
                                {...field}
                                data-testid="input-trigger-key"
                              />
                            </FormControl>
                            <FormDescription>
                              Unique identifier (lowercase, numbers, underscores, hyphens only)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Display Name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g., User Onboarding Start"
                                {...field}
                                data-testid="input-trigger-name"
                              />
                            </FormControl>
                            <FormDescription>Human-readable name for the trigger</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Describe what this trigger does and when it should activate..."
                              {...field}
                              data-testid="input-trigger-description"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="source"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Trigger Source</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(value);
                                setSelectedEventTypes([]);
                                form.setValue('sourceEventTypes', []);
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger data-testid="select-trigger-source">
                                  <SelectValue placeholder="Select source type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="crm">
                                  <div className="flex items-center">
                                    <Database className="mr-2 h-4 w-4" />
                                    CRM Events
                                  </div>
                                </SelectItem>
                                <SelectItem value="webhook">
                                  <div className="flex items-center">
                                    <Webhook className="mr-2 h-4 w-4" />
                                    Webhook Events
                                  </div>
                                </SelectItem>
                                <SelectItem value="time">
                                  <div className="flex items-center">
                                    <Clock className="mr-2 h-4 w-4" />
                                    Time-based
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Status</FormLabel>
                            <div className="flex items-center space-x-2">
                              <Switch
                                checked={field.value === 'active'}
                                onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'disabled')}
                                data-testid="switch-trigger-status"
                              />
                              <Label>{field.value === 'active' ? 'Active' : 'Disabled'}</Label>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="events" className="space-y-4">
                    <div>
                      <Label className="text-base font-semibold">Event Types</Label>
                      <p className="text-sm text-muted-foreground mb-4">
                        Select the events that should trigger this automation
                      </p>

                      {/* Predefined Event Types */}
                      {form.watch('source') && (
                        <div className="space-y-4">
                          <div>
                            <Label className="text-sm font-medium">Predefined Events for {form.watch('source').toUpperCase()}</Label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                              {EVENT_TYPES[form.watch('source') as keyof typeof EVENT_TYPES]?.map((eventType) => (
                                <Button
                                  key={eventType}
                                  type="button"
                                  variant={selectedEventTypes.includes(eventType) ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => {
                                    if (selectedEventTypes.includes(eventType)) {
                                      removeEventType(eventType);
                                    } else {
                                      addEventType(eventType);
                                    }
                                  }}
                                  className="justify-start"
                                  data-testid={`button-event-type-${eventType}`}
                                >
                                  {selectedEventTypes.includes(eventType) && <Check className="mr-2 h-3 w-3" />}
                                  {eventType}
                                </Button>
                              ))}
                            </div>
                          </div>

                          <Separator />

                          {/* Custom Event Type */}
                          <div>
                            <Label className="text-sm font-medium">Add Custom Event Type</Label>
                            <div className="flex space-x-2 mt-2">
                              <Input
                                placeholder="custom_event_type"
                                value={customEventType}
                                onChange={(e) => setCustomEventType(e.target.value)}
                                data-testid="input-custom-event-type"
                              />
                              <Button
                                type="button"
                                onClick={addCustomEventType}
                                disabled={!customEventType}
                                data-testid="button-add-custom-event"
                              >
                                Add
                              </Button>
                            </div>
                          </div>

                          {/* Selected Event Types */}
                          {selectedEventTypes.length > 0 && (
                            <div>
                              <Label className="text-sm font-medium">Selected Event Types</Label>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {selectedEventTypes.map((eventType) => (
                                  <Badge key={eventType} variant="secondary" className="flex items-center gap-1">
                                    {eventType}
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-auto p-0 ml-1"
                                      onClick={() => removeEventType(eventType)}
                                      data-testid={`button-remove-event-${eventType}`}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {selectedEventTypes.length === 0 && (
                            <Alert>
                              <Info className="h-4 w-4" />
                              <AlertDescription>
                                Please select at least one event type for this trigger.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="advanced" className="space-y-4">
                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="payloadSchema"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Payload Schema (JSON)</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder='{"userId": "number", "eventData": "object"}'
                                className="font-mono text-sm"
                                rows={6}
                                {...field}
                                data-testid="input-payload-schema"
                              />
                            </FormControl>
                            <FormDescription>
                              JSON schema to validate incoming trigger payloads (optional)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="defaultFilter"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Default Filter Conditions (JSON)</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder='{"and": [{">=": [{"var": "user.tier"}, 1]}, {"==": [{"var": "user.active"}, true]}]}'
                                className="font-mono text-sm"
                                rows={6}
                                {...field}
                                data-testid="input-default-filter"
                              />
                            </FormControl>
                            <FormDescription>
                              JSONLogic conditions to filter when this trigger should activate (optional)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Alert>
                        <Info className="h-4 w-4" />
                        <AlertDescription>
                          <strong>JSONLogic Examples:</strong><br />
                          • User tier check: <code>{'{">=": [{"var": "user.tier"}, 2]}'}</code><br />
                          • Multiple conditions: <code>{'{"and": [{"==": [{"var": "user.active"}, true]}, {">=": [{"var": "user.signupDays"}, 7]}]}'}</code><br />
                          • Visit <a href="https://jsonlogic.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">jsonlogic.com</a> for more examples
                        </AlertDescription>
                      </Alert>
                    </div>
                  </TabsContent>
                </Tabs>

                <DialogFooter className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsCreateDialogOpen(false);
                      setIsEditDialogOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending || selectedEventTypes.length === 0}
                    data-testid="button-save-trigger"
                  >
                    {createMutation.isPending || updateMutation.isPending ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {editingTrigger ? 'Updating...' : 'Creating...'}
                      </div>
                    ) : (
                      editingTrigger ? 'Update Trigger' : 'Create Trigger'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Preview Trigger Evaluation</DialogTitle>
              <DialogDescription>
                Test this trigger with sample data to see if it would match
              </DialogDescription>
            </DialogHeader>

            {previewTrigger && (
              <div className="space-y-4">
                <div>
                  <Label className="font-semibold">Trigger: {previewTrigger.name}</Label>
                  <p className="text-sm text-muted-foreground">{previewTrigger.description}</p>
                </div>

                <div>
                  <Label>Test Data (JSON)</Label>
                  <Textarea
                    placeholder='{"userId": 123, "eventType": "user_registered", "eventData": {"email": "test@example.com"}}'
                    className="font-mono text-sm"
                    rows={6}
                    data-testid="input-preview-test-data"
                  />
                </div>

                <div className="flex space-x-2">
                  <Button
                    onClick={() => {
                      const testDataElement = document.querySelector('[data-testid="input-preview-test-data"]') as HTMLTextAreaElement;
                      if (testDataElement?.value) {
                        try {
                          const testData = JSON.parse(testDataElement.value);
                          previewMutation.mutate({ triggerId: previewTrigger.id, testData });
                        } catch (error) {
                          toast({
                            title: "Invalid JSON",
                            description: "Please enter valid JSON test data",
                            variant: "destructive",
                          });
                        }
                      }
                    }}
                    disabled={previewMutation.isPending}
                    data-testid="button-run-preview"
                  >
                    {previewMutation.isPending ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Testing...
                      </div>
                    ) : (
                      <>
                        <Play className="mr-2 h-4 w-4" />
                        Run Test
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setIsPreviewDialogOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}